// src/hooks/useLeftSecEvents.ts
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import  useWebSocketContext  from './useWebSocketContext'; 
import { resetChatThunk } from '../../store/slices/resetChatThunks';
import { setKbStatus } from '../../store/slices/kbSlice';
import { incrementProcessingCount } from '../../store/slices/fileProcessingSlice';
import { setSessionId } from '../../store/slices/sessionSlice';
import { v4 as uuidv4 } from 'uuid';
import { userMessage } from '../../store/slices/chatSlice';

const useLeftSecEvents = () => {
    const [isHovered, setIsHovered] = useState(false);
    const [isLogoHovered, setIsLogoHovered] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const messages = useSelector((state: RootState) => state.chat.messages);
    const { session_id, user_id } = useSelector((state: RootState) => state.session);
    const activeChatGroup = useSelector((state: RootState) => state.chatHistory.activeChatGroup);
    const { sendBinaryToWs, sendToWs } = useWebSocketContext(); 
    const [isClicked, setIsClicked] = useState<boolean>(false);
    const isFileProcessing = useSelector((state: RootState) => state.fileProcessing.isFileProcessing);
    
    const getMethodForFileName = (fileName: string): string => {
        console.log(`Determining method for file: ${fileName}`);
        const upperName = fileName.toUpperCase();
        if (upperName.includes('MEL')) return 'load_mel_application';
        if (['AACOMAS', 'AMCAS'].some(keyword => upperName.includes(keyword))) return 'load_sgu_application';
        if (upperName.includes('IL_PR')) return 'generate_fcic_fnol';
        if (upperName.includes('FNOL')) return 'process_fcic_claim';
        return 'load_docs';
    };

    // Add new function to get the appropriate message
    const getUploadMessage = (fileName: string): string => {
        const upperName = fileName.toUpperCase();
        if (upperName.includes('MEL')) return `Uploaded MEL application: ${fileName}`;
        if (['AACOMAS', 'AMCAS'].some(keyword => upperName.includes(keyword))) return `Uploaded SGU application: ${fileName}`;
        if (upperName.includes('IL_PR') || upperName.includes('FNOL')) return `Generated FCIC FNOL: ${fileName}`;
        return `Uploaded document: ${fileName}`; // Default message for unknown types
    };

    const handleResetChat = () => {
        console.log('Attempting to reset chat...');
        if (messages.length > 0) {
            console.log('Messages found, dispatching resetChatThunk and clearing KB');
            dispatch(resetChatThunk());
            // Send a message to the backend to clear the KB
            const msgToSend = JSON.stringify({
                method: 'clear_kb',
                params: {
                    session_id: session_id,
                    user_id: user_id,
                    agent: 'keai',
                },
            });
            sendToWs(msgToSend);

            console.log('Generating new session ID...');
            const newSessionId = uuidv4();
            dispatch(setSessionId(newSessionId));
            console.log('Generated new session ID:', newSessionId);
        }
    };

    const sendMetadataAndBinaryData = (metadata: string, binaryData: Blob) => {
        const delimiter = "\0";
        const combinedMessage = new Blob([metadata, delimiter, binaryData]);
        sendBinaryToWs(combinedMessage);
    };

    const processFiles = (files: FileList) => {
        console.log(`Processing ${files.length} file(s)`);
        dispatch(setKbStatus('loading'));

        const supportedTypes = new Map([
            ['application/pdf', 'pdf'],
            ['text/plain', 'txt'],
            ['text/csv', 'csv'],
            ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx'],
            ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx'],
            ['application/vnd.ms-excel.sheet.macroEnabled.12', 'xlsm'],
            ['application/vnd.ms-excel', 'xls']
        ]);

        const supportedExtensions = ['.pdf', '.txt', '.csv', '.docx', '.xlsx', '.xlsm', '.xls'];

        const validFiles = Array.from(files).filter((file: File) => {
            // Check by MIME type first
            if (supportedTypes.has(file.type)) {
                return true;
            }
            // Fallback: Check by file extension if MIME type is not recognized
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            return supportedExtensions.includes(fileExtension);
        });

        if (validFiles.length === 0) {
            console.error('No supported files found. Please upload PDF, TXT, CSV, DOCX, XLSX, XLSM, or XLS files.');
            return;
        }

        validFiles.forEach((file: File) => {
            console.log(`Selected file: ${file.name}`);
            // Use the new function to get the appropriate message
            dispatch(userMessage(getUploadMessage(file.name)));
            dispatch(incrementProcessingCount());

            // Get the file type from the supported types map, or fallback to extension
            let fileType = supportedTypes.get(file.type);
            if (!fileType) {
                // Fallback: determine type from file extension
                const fileExtension = file.name.split('.').pop()?.toLowerCase();
                fileType = fileExtension || 'pdf';
            }
            console.log('fileType', fileType);
            const methodToSend = getMethodForFileName(file.name);
            
            const params: {
                agent: string;
                session_id: string | null;
                user_id: string | null;
                doc_name: string;
                file_type: string;
                chat_group_id?: string;
            } = {
                agent: 'keai',
                session_id: session_id,
                user_id: user_id,
                doc_name: file.name,
                file_type: fileType 
            };
            
            // Include chat_group_id if an active chat group exists
            if (activeChatGroup) {
                params.chat_group_id = activeChatGroup;
            }
            
            const metadata = JSON.stringify({
                method: methodToSend,
                params: params
            });
            
            console.log(`Sending file to backend with method: ${methodToSend}`);
            sendMetadataAndBinaryData(metadata, file);
            console.log(`File sent`);
        });
    };

    const processUrl = (url: string) => {
        console.log(`Valid URL detected: ${url}, sending to backend...`);
        dispatch(setKbStatus('loading'));
        dispatch(incrementProcessingCount());

        console.log(`Sending URL to backend: ${url}`);
        const urlData = JSON.stringify({
            method: 'load_url',
            params: {
                agent: 'keai',
                session_id: session_id,
                user_id: user_id,
                url: url,
                file_type: 'url'
            }
        });

        sendToWs(urlData);
    };

    const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        // CRITICAL: Prevent default FIRST to stop Safari from navigating to PDF
        event.preventDefault();
        event.stopPropagation();

        // Try dataTransfer.files first (works in most browsers)
        const files = event.dataTransfer.files;

        if (files && files.length > 0) {
            processFiles(files);
            return;
        }

        // Fallback: Safari fix for downloads section
        // Safari doesn't populate dataTransfer.files when dragging from downloads
        const items = event.dataTransfer.items;

        if (items.length === 0) {
            console.error('No items dropped');
            return;
        }

        // Try to get files from items
        const filesArray: File[] = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    filesArray.push(file);
                }
            }
        }

        if (filesArray.length > 0) {
            // Create a FileList-like object
            const fileList = Object.assign(filesArray, {
                item: (index: number) => filesArray[index] || null
            }) as unknown as FileList;
            processFiles(fileList);
            return;
        }

        // If no files found, check for URLs
        for (const item of items) {
            if (item.kind === 'string' && item.type.match('^text/plain')) {
                item.getAsString((text) => {
                    if (isValidUrl(text)) {
                        processUrl(text);
                    } else {
                        console.error('Dropped text is not a valid URL');
                    }
                });
                break;
            }
        }
    };

    const isValidUrl = (urlString: string): boolean => {
        try {
            new URL(urlString);
            return true;
        } catch {
            return false;
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) {
            console.error('Error reading files');
            return;
        }
        processFiles(files);
    };

    return {
        isHovered,
        setIsHovered,
        isLogoHovered,
        setIsLogoHovered,
        isFileProcessing,
        handleResetChat,
        handleFileDrop,
        handleFileSelect,
        isClicked,
        setIsClicked,
    };
};

export default useLeftSecEvents;
